import { useSchema, DEFAULT_SEOMATIC_SCHEMA } from '@/composables/useSchema';

/**
 *
 * @param {Sting} metaTagContainerJson - craft seomatic metaTagContainer Json
 * @returns {Array} of metafield Objects with meta-field attributes
 */
export const metaTagsTransformer = (metaTagContainerJson) => {
  if (typeof metaTagContainerJson !== 'string') return [];

  let metas;
  try {
    metas = JSON.parse(metaTagContainerJson);
  } catch (e) {
    console.warn('[Seomatic] Failed to parse metaTagContainer:', e.message);
    return [];
  }
  return Object.entries(metas).reduce((acc, [hid, value]) => {
    // remove empty fields identified by empty array
    if (Array.isArray(value) && value.length === 0) return acc;
    // reduce array fields
    if (Array.isArray(value)) {
      return acc.concat(value.map((val, index) => ({ ...val, hid: `${hid}-${index}` })));
    }

    return acc.concat({ ...value, hid });
  }, []);
};

// only the Swiss locales are served, see i18n.locales in nuxt.config.js
const SERVED_HREFLANGS = ['de-ch', 'fr-ch'];

const isServedHreflang = (link) => !link?.hreflang || SERVED_HREFLANGS.includes(link.hreflang.toLowerCase());

/**
 *
 * @param {string} metaLinkContainerJson - craft Seomatic link tags
 * @returns {array} - array of objects with link-tag attributes
 */
export const metaLinkTransformer = (metaLinkContainerJson) => {
  if (typeof metaLinkContainerJson !== 'string') return [];

  let metas;
  try {
    metas = JSON.parse(metaLinkContainerJson);
  } catch (e) {
    console.warn('[Seomatic] Failed to parse metaLinkContainer:', e.message);
    return [];
  }
  return Object.entries(metas).reduce((acc, [rel, value]) => {
    // ignore author tag
    if (rel === 'author') return acc;
    // remove empty fields identified by empty array
    if (Array.isArray(value) && value.length === 0) return acc;
    // reduce array fields, dropping hreflang links to Craft sites that are not served here
    if (Array.isArray(value)) {
      return acc.concat(value.filter(isServedHreflang).map((val) => ({ ...val, rel })));
    }

    if (!isServedHreflang(value)) return acc;

    return acc.concat({ ...value, rel });
  }, []);
};

export const metaTitleTransformer = (metaTitleContainerJson) => {
  if (typeof metaTitleContainerJson !== 'string') return null;
  try {
    return JSON.parse(metaTitleContainerJson)?.title?.title;
  } catch (e) {
    console.warn('[Seomatic] Failed to parse metaTitleContainer:', e.message);
    return null;
  }
};

export const metaJsonLdTransformer = (metaJsonLdContainer, entry = null) => {
  if (typeof metaJsonLdContainer !== 'string') return [];

  let schema;
  try {
    schema = JSON.parse(metaJsonLdContainer);
  } catch (e) {
    console.warn('[Seomatic] Failed to parse metaJsonLdContainer:', e.message);
    return [];
  }
  const { $localeRootPath } = useNuxtApp();

  // Use SEOMatic data as-is for core fields, only set defaults if missing
  if (!schema['@context']) schema['@context'] = 'https://schema.org';
  if (!schema['@type']) schema['@type'] = 'WebSite';

  const baseUrl = schema.url || 'https://blackroll.com';

  // Add SearchAction for Google search box
  if (!schema.potentialAction) {
    schema.potentialAction = {
      '@type': 'SearchAction',
      target: `${baseUrl}${$localeRootPath('/search')}?query={search_term_string}`,
      'query-input': 'required name=search_term_string',
    };
  }

  // overwrite author with expert
  try {
    if (entry?.expert && entry.expert.length > 0) {
      const expert = entry.expert[0];

      schema.mainEntityOfPage.author = {
        '@type': 'Person',
        name: expert?.title,
        url: baseUrl + $localeRootPath(expert.uri),
      };
    }
  } catch (e) {
    console.log(e);
  }

  // Clean up SEOMatic internal fields not needed in output
  try {
    delete schema.identity;
    delete schema.breadcrumbList;
  } catch (e) {
    console.log(e);
  }

  return schema;
};

/**
 *
 * @param {object} seomatic - carft gql seomatic partical data
 * @returns {object} - nuxt head fromated seomatic data
 */
export const seomaticHead = (seomatic, entry = null) => {
  const head = {};

  const { registerComponentSchema } = useSchema();
  const seomaticSchemaOrg = metaJsonLdTransformer(seomatic?.metaJsonLdContainer, entry);

  registerComponentSchema(seomaticSchemaOrg, DEFAULT_SEOMATIC_SCHEMA);

  head.meta = metaTagsTransformer(seomatic?.metaTagContainer);
  head.link = metaLinkTransformer(seomatic?.metaLinkContainer);
  const title = metaTitleTransformer(seomatic?.metaTitleContainer);

  if (title) head.title = title;

  return head;
};
